// import { useIdleTimer } from 'react-idle-timer';
// import { useDispatch } from 'react-redux';
// import { logoutUser } from '../../AppSystem/Redux/Auth/AuthSlice';
// import { redirectWhenCloseSession } from './RedirectWhenCloseSession';
// import { jwtDecode } from 'jwt-decode';
// import { useMsal } from '@azure/msal-react';
// import { useSelector } from 'react-redux';
// import { PUBLIC_PATHS } from '../../config';
// import { CustomReload } from '../../AppSystem/Helpers/CustomReload';
// import { useEffect } from 'react';

// const SessionManager = ({ children }) => {
//     const { tokenData, refreshTokenData } = useSelector(state => state.auth);
//     const { instance, accounts } = useMsal();
//     const activeAccount = instance.getActiveAccount() || accounts[0];
//     const dispatch = useDispatch();
  
//     const onIdle = async () => {
//         try {
//             localStorage.setItem('session_logout', Date.now().toString());

//             if (tokenData) {
//                 const decodedToken = jwtDecode(tokenData);
//                 const loginMethod = decodedToken.loginMethod;
                
//                 dispatch(logoutUser());
                
//                 if (loginMethod?.toLowerCase() === 'lrfssoentraid') {

//                     if (activeAccount) {

//                         await instance.logoutRedirect({ account: activeAccount });
//                     } else {

//                         // await instance.logoutRedirect();
//                         //CustomReload(PUBLIC_PATHS.APP);
//                     }

//                 } else {

//                     redirectWhenCloseSession(tokenData);
//                 }
//             }
//         } catch (error) {
//             dispatch(logoutUser());
//             CustomReload(PUBLIC_PATHS.APP);
//         }
//     };

//     useIdleTimer({
//         // Tiempo máximo de inactividad (capa adicional de seguridad).
//         // Ahora la expiración real de sesión se controla principalmente
//         // por la expiración del token/refresh token.
//         timeout: 1000 * 60 * 60 * 4, // 4 horas
//         onIdle,
//         crossTab: true,
//         leaderElection: true,
//         syncTimers: 200
//     });

//     // Si el usuario vuelve con el navegador recién abierto y el token
//     // o refresh token ya está expirado, forzamos logout inmediato.
//     useEffect(() => {
//         if (!refreshTokenData?.expires) return;

//         console.log('[Session][SessionManager] refreshTokenData.expires cambiado a:', refreshTokenData.expires);

//         const end = new Date(refreshTokenData.expires);
//         if (isNaN(end.getTime())) return;

//         const now = new Date();
//         if (end <= now) {
//             // Reutilizamos la misma lógica de cierre de sesión.
//             console.log('[Session][SessionManager] refresh token ya expirado al montar. Ejecutando onIdle()');
//             onIdle();
//         }
//         // eslint-disable-next-line react-hooks/exhaustive-deps
//     }, [refreshTokenData?.expires]);

//     useEffect(() => {
//         const handleStorageChange = (e) => {
//             if (e.key === 'session_logout' && e.newValue !== null) {
//                 dispatch(logoutUser());
//                 CustomReload(PUBLIC_PATHS.APP);
//             }
//         };

//         window.addEventListener('storage', handleStorageChange);
//         return () => window.removeEventListener('storage', handleStorageChange);
//     }, [dispatch]);

//     return <>{children}</>;
// };

// export default SessionManager;

import { useIdleTimer } from 'react-idle-timer';
import { useDispatch, useSelector } from 'react-redux';
import { logoutUser } from '../../AppSystem/Redux/Auth/AuthSlice';
import { redirectWhenCloseSession } from './RedirectWhenCloseSession';
import { jwtDecode } from 'jwt-decode';
import { useMsal } from '@azure/msal-react';
import { PUBLIC_PATHS } from '../../config';
import { CustomReload } from '../../AppSystem/Helpers/CustomReload';
import { useEffect, useRef, useCallback } from 'react';

const SessionManager = ({ children }) => {
    const { tokenData, refreshTokenData } = useSelector(state => state.auth);
    const { instance, accounts } = useMsal();
    const dispatch = useDispatch();
    
    // Ref to prevent multiple concurrent logout executions
    const isLoggingOut = useRef(false);
    
    // Memoize the logout function to avoid unnecessary recreations
    const handleLogout = useCallback(async () => {
        // Prevent concurrent executions
        if (isLoggingOut.current) {
            console.log('[Session][SessionManager] Logout already in progress, ignoring duplicate call');
            return;
        }
        
        isLoggingOut.current = true;
        
        try {
            localStorage.setItem('session_logout', Date.now().toString());

            if (tokenData) {
                let loginMethod = null;
                
                try {
                    const decodedToken = jwtDecode(tokenData);
                    loginMethod = decodedToken.loginMethod;
                } catch (decodeError) {
                    console.error('[Session][SessionManager] Error decoding token:', decodeError);
                    // Continue with generic logout if the decoding fails
                }
                
                dispatch(logoutUser());
                
                if (loginMethod?.toLowerCase() === 'lrfssoentraid') {
                    const activeAccount = instance.getActiveAccount() || accounts[0];
                    
                    if (activeAccount) {
                        await instance.logoutRedirect({ account: activeAccount });
                    } else {
                        console.warn('[Session][SessionManager] No active account for EntraID, redirecting to login');
                       // CustomReload(PUBLIC_PATHS.APP);
                    }
                } else {
                    redirectWhenCloseSession(tokenData);
                }
            } else {
                    // If there is no token, do generic logout
                dispatch(logoutUser());
                CustomReload(PUBLIC_PATHS.APP);
            }
        } catch (error) {
            console.error('[Session][SessionManager] Error during logout:', error);
            dispatch(logoutUser());
            CustomReload(PUBLIC_PATHS.APP);
        } finally {
            // Reset the flag after a delay to allow new attempts if necessary
            setTimeout(() => {
                isLoggingOut.current = false;
            }, 1000);
        }
    }, [tokenData, instance, accounts, dispatch]);

    // useIdleTimer with the memoized function
    useIdleTimer({
        timeout: 1000 * 60 * 60 * 4, // 4 horas
        onIdle: handleLogout,
        crossTab: true,
        leaderElection: true,
        syncTimers: 200
    });

    // Check the expiration of the refresh token when it is mounted and when it changes
    useEffect(() => {
        if (!refreshTokenData?.expires) return;

        console.log('[Session][SessionManager] Checking refresh token expiration:', refreshTokenData.expires);

        const checkTokenExpiration = () => {
            try {
                const expirationDate = new Date(refreshTokenData.expires);
                
                // Verify that the date is valid
                if (isNaN(expirationDate.getTime())) {
                    console.error('[Session][SessionManager] Invalid expiration date:', refreshTokenData.expires);
                    return;
                }

                const now = new Date();
                const timeUntilExpiration = expirationDate.getTime() - now.getTime();
                
                // If it has expired
                if (timeUntilExpiration <= 0) {
                    console.log('[Session][SessionManager] Refresh token expired, closing session');
                    handleLogout();
                    return;
                }
                
                // Optional: warning when there is little time left (e.g. 5 minutes)
                if (timeUntilExpiration < 5 * 60 * 1000) {
                    console.warn('[Session][SessionManager] The refresh token will expire soon.');
                }
                
            } catch (error) {
                console.error('[Session][SessionManager] Error checking expiration:', error);
            }
        };

        checkTokenExpiration();
        
        // Check periodically (every minute)
        const intervalId = setInterval(checkTokenExpiration, 60 * 1000);
        
        return () => clearInterval(intervalId);
    }, [refreshTokenData?.expires, handleLogout]);

    useEffect(() => {
        const handleStorageChange = (e) => {
            // Verify that the event has the correct key and a new value.
            if (e.key === 'session_logout' && e.newValue !== null) {
                const logoutTimestamp = parseInt(e.newValue, 10);
                const now = Date.now();
                
                // Verify that the logout event is recent (less than 5 seconds ago)
                // to avoid processing old events
                if (now - logoutTimestamp < 5000) {
                    console.log('[Session][SessionManager] Logout detected in another tab, closing session');
                    dispatch(logoutUser());
                    CustomReload(PUBLIC_PATHS.APP);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [dispatch]);

    useEffect(() => {
        return () => {
            isLoggingOut.current = false;
        };
    }, []);

    return <>{children}</>;
};

export default SessionManager;